/**
 * G008 — 施工、拆除与物流。
 *
 * 材料三态账本（reserved -> delivered -> consumed）贯穿全流程：
 * 三态之和 + 已退款 恒等于造价 —— 材料不重复扣除、不凭空复制。
 *
 * 物流是【区域批次】：每 Tick 一个全局配送预算，按实体确定序分给
 * 可达工地；不模拟单件物品。可达性 = 建筑任一占用格与「接入库房的
 * 道路连通分量」相邻。不可达 -> 配送停摆 + 一次性 DeliveryBlocked
 * 告警（原因稳定码），恢复时发 DeliveryResumed。
 *
 * 工时由共享施工班组贡献：材料齐备的工地按确定序分配工人，
 * progress 达到 workMinutes 稳定转换为 operational（BuildingCompleted）。
 */

import type { BuildCellId } from '../spatial/types.js';
import { cellKey } from '../spatial/types.js';
import { getCell } from '../spatial/grid.js';
import { neighbors, releaseCells } from '../spatial/ops.js';
import type { CommandHandler, SimulationSystem } from '../system.js';
import type { SimulationContext } from '../context.js';
import { MINUTES_PER_TICK } from '../kernel.js';
import {
  BUILDING_COMPONENT,
  listBuildings,
  type BuildingComponent,
  type PlanBuildingSpec,
} from './plan-building.js';

/* ================= 世界状态扩展 ================= */

/** 施工/物流领域状态（纯数据，入快照与哈希）。 */
export interface ConstructionState {
  /** 道路格集合（cellKey -> true）。库房格视为天然道路。 */
  roadCells: Record<string, true>;
  /** 库房（配送源点）。 */
  depot: BuildCellId;
  /** 共享施工班组人数。 */
  workforce: number;
}

export function createEmptyConstructionState(): ConstructionState {
  return {
    roadCells: {},
    depot: { layer: 'floor', ring: 0, sector: 0 },
    workforce: 8,
  };
}

/** WorldState 里的字段名（G008 附加；createEmptyWorld 负责初始化）。 */
export const CONSTRUCTION_STATE_KEY = 'construction';

interface WorldWithConstruction {
  construction: ConstructionState;
}

function constructionOf(ctx: SimulationContext): ConstructionState {
  return (ctx.state as unknown as WorldWithConstruction).construction;
}

/* ================= 稳定码 ================= */

export type ConstructionRejection =
  | 'ROAD_CELL_INVALID'
  | 'ROAD_CELL_OCCUPIED'
  | 'ROAD_ALREADY_EXISTS'
  | 'ROAD_RESOURCES_INSUFFICIENT'
  | 'CANCEL_UNKNOWN_ENTITY'
  | 'CANCEL_INVALID_STATE'
  | 'DECON_UNKNOWN_ENTITY'
  | 'DECON_INVALID_STATE'
  | 'DAMAGE_UNKNOWN_ENTITY'
  | 'DAMAGE_INVALID_STATE';

/** 配送阻塞原因（写进 component.blocked 与 DeliveryBlocked 事件）。 */
export const LOGISTICS_UNREACHABLE = 'LOGISTICS_UNREACHABLE';

/* ================= 工具 ================= */

function sumValues(m: Readonly<Record<string, number>>): number {
  let n = 0;
  for (const v of Object.values(m)) n += v;
  return n;
}

function getBuilding(ctx: SimulationContext, entityId: string): BuildingComponent | null {
  const bucket = ctx.state.entities.components[BUILDING_COMPONENT];
  const c = bucket?.[entityId];
  return c ? (c as unknown as BuildingComponent) : null;
}

function removeEntity(ctx: SimulationContext, entityId: string): void {
  const entities = ctx.state.entities;
  const i = entities.order.indexOf(entityId as (typeof entities.order)[number]);
  if (i >= 0) entities.order.splice(i, 1);
  delete entities.kind[entityId];
  const bucket = entities.components[BUILDING_COMPONENT];
  if (bucket) delete bucket[entityId];
}

function refundInto(
  stock: Record<string, number>,
  refund: Record<string, number>,
  source: Readonly<Record<string, number>>,
  basisPoints: number,
): void {
  for (const [res, amount] of Object.entries(source)) {
    const back = Math.floor((amount * basisPoints) / 10000);
    if (back <= 0) continue;
    stock[res] = (stock[res] ?? 0) + back;
    refund[res] = (refund[res] ?? 0) + back;
  }
}

/* ================= 道路可达性 ================= */

/**
 * 计算「接入库房的道路连通分量」所覆盖 + 相邻的格集合。
 * 每 Tick 全量 BFS：道路规模小、且必须确定性，不做增量缓存。
 */
export function reachableCellKeys(
  ctx: SimulationContext,
  cons: ConstructionState,
): Set<string> {
  const map = ctx.state.spatial;
  const roads = new Set<string>(Object.keys(cons.roadCells));
  roads.add(cellKey(cons.depot));                 // 库房格是天然源点
  // BFS 找与库房连通的道路格
  const connected = new Set<string>();
  const queue: BuildCellId[] = [cons.depot];
  connected.add(cellKey(cons.depot));
  while (queue.length) {
    const cur = queue.shift();
    if (!cur) break;
    for (const n of neighbors(map, cur)) {
      const k = cellKey(n);
      if (roads.has(k) && !connected.has(k)) {
        connected.add(k);
        queue.push(n);
      }
    }
  }
  // 可达 = 连通道路格自身 + 其邻格
  const out = new Set<string>(connected);
  for (const k of connected) {
    const [layer, ring, sector] = k.split(':');
    const id: BuildCellId = {
      layer: layer as BuildCellId['layer'],
      ring: Number(ring),
      sector: Number(sector),
    };
    for (const n of neighbors(map, id)) out.add(cellKey(n));
  }
  return out;
}

export function isBuildingReachable(
  ctx: SimulationContext,
  cons: ConstructionState,
  building: BuildingComponent,
): boolean {
  const reach = reachableCellKeys(ctx, cons);
  return building.cells.some((c) => reach.has(cellKey(c)));
}

/* ================= 命令 ================= */

export const LAY_ROAD = 'lay_road';
export interface LayRoadPayload { readonly cell: BuildCellId }
export const ROAD_COST: Readonly<Record<string, number>> = { metal: 2 };

export function createLayRoadHandler(): CommandHandler<LayRoadPayload> {
  return {
    type: LAY_ROAD,
    apply(ctx, payload) {
      const cell = payload?.cell;
      const mapCell = cell ? getCell(ctx.state.spatial, cell) : undefined;
      if (!mapCell) return { accepted: false, rejectionCode: 'ROAD_CELL_INVALID' };
      if (mapCell.occupiedBy !== null) return { accepted: false, rejectionCode: 'ROAD_CELL_OCCUPIED' };
      const cons = constructionOf(ctx);
      const key = cellKey(cell);
      if (cons.roadCells[key]) return { accepted: false, rejectionCode: 'ROAD_ALREADY_EXISTS' };
      const stock = ctx.state.resources.stock as Record<string, number>;
      for (const [res, amount] of Object.entries(ROAD_COST)) {
        if ((stock[res] ?? 0) < amount) {
          return { accepted: false, rejectionCode: 'ROAD_RESOURCES_INSUFFICIENT' };
        }
      }
      for (const [res, amount] of Object.entries(ROAD_COST)) {
        stock[res] = (stock[res] ?? 0) - amount;
      }
      cons.roadCells[key] = true;
      ctx.emit({ type: 'RoadLaid', payload: { cell } });
      return { accepted: true };
    },
  };
}

export const CANCEL_BUILDING = 'cancel_building';
export interface CancelBuildingPayload { readonly entityId: string }

/**
 * 取消：只允许 planned / under_construction。
 * 退款按已消耗阶段：未送达(reserved) 与 已送达未消耗(delivered) 全额，
 * 已消耗(consumed) 按 refundBasisPoints 折算。
 */
export function createCancelBuildingHandler(
  getDef: (defId: string) => PlanBuildingSpec | undefined,
): CommandHandler<CancelBuildingPayload> {
  return {
    type: CANCEL_BUILDING,
    apply(ctx, payload) {
      const b = payload?.entityId ? getBuilding(ctx, payload.entityId) : null;
      if (!b) return { accepted: false, rejectionCode: 'CANCEL_UNKNOWN_ENTITY' };
      if (b.lifecycle !== 'planned' && b.lifecycle !== 'under_construction') {
        return { accepted: false, rejectionCode: 'CANCEL_INVALID_STATE' };
      }
      const def = getDef(b.defId);
      const bp = def?.refundBasisPoints ?? 0;
      const stock = ctx.state.resources.stock as Record<string, number>;
      const refund: Record<string, number> = {};
      refundInto(stock, refund, b.reserved, 10000);
      refundInto(stock, refund, b.delivered, 10000);
      refundInto(stock, refund, b.consumed, bp);
      releaseCells(ctx.state.spatial, b.cells);
      removeEntity(ctx, payload.entityId);
      ctx.emit({ type: 'BuildingCancelled', payload: { entityId: payload.entityId, refund } });
      return { accepted: true };
    },
  };
}

export const DECONSTRUCT_BUILDING = 'deconstruct_building';
export interface DeconstructBuildingPayload { readonly entityId: string }

/** 拆除：只允许 operational / damaged；整体按 refundBasisPoints 回收。 */
export function createDeconstructBuildingHandler(
  getDef: (defId: string) => PlanBuildingSpec | undefined,
): CommandHandler<DeconstructBuildingPayload> {
  return {
    type: DECONSTRUCT_BUILDING,
    apply(ctx, payload) {
      const b = payload?.entityId ? getBuilding(ctx, payload.entityId) : null;
      if (!b) return { accepted: false, rejectionCode: 'DECON_UNKNOWN_ENTITY' };
      if (b.lifecycle !== 'operational' && b.lifecycle !== 'damaged') {
        return { accepted: false, rejectionCode: 'DECON_INVALID_STATE' };
      }
      const def = getDef(b.defId);
      const bp = def?.refundBasisPoints ?? 0;
      const stock = ctx.state.resources.stock as Record<string, number>;
      const refund: Record<string, number> = {};
      refundInto(stock, refund, b.consumed, bp);
      releaseCells(ctx.state.spatial, b.cells);
      removeEntity(ctx, payload.entityId);
      ctx.emit({ type: 'BuildingDeconstructed', payload: { entityId: payload.entityId, refund } });
      return { accepted: true };
    },
  };
}

export const MARK_BUILDING_DAMAGED = 'mark_building_damaged';
export interface MarkDamagedPayload { readonly entityId: string }

/** 损坏前的基础状态转换：operational -> damaged。灾害触发属后续 Goal。 */
export function createMarkDamagedHandler(): CommandHandler<MarkDamagedPayload> {
  return {
    type: MARK_BUILDING_DAMAGED,
    apply(ctx, payload) {
      const b = payload?.entityId ? getBuilding(ctx, payload.entityId) : null;
      if (!b) return { accepted: false, rejectionCode: 'DAMAGE_UNKNOWN_ENTITY' };
      if (b.lifecycle !== 'operational') {
        return { accepted: false, rejectionCode: 'DAMAGE_INVALID_STATE' };
      }
      b.lifecycle = 'damaged';
      ctx.emit({ type: 'BuildingDamaged', payload: { entityId: payload.entityId } });
      return { accepted: true };
    },
  };
}

/* ================= 系统 ================= */

export const SPEND_RESOURCES = 'spend_resources';
export interface SpendResourcesPayload {
  readonly cost: Readonly<Record<string, number>>;
  /** 用途标记，进事件便于审计（如 'infrastructure:pylon'）。 */
  readonly reason: string;
}

/**
 * spend_resources — 为**尚未建模为实体**的支出扣账（当前：基建段件）。
 *
 * 这是一条过渡命令，存在的理由是宁可让这类支出走内核这本唯一的账，也不要
 * 在渲染层留第二本账本 —— 后者正是本次接线要消灭的东西。等基建成为实体
 * （有自己的放置命令与生命周期）后，本命令应当撤除。
 */
export function createSpendResourcesHandler(): CommandHandler<SpendResourcesPayload> {
  return {
    type: SPEND_RESOURCES,
    apply(ctx, payload) {
      const cost = payload?.cost;
      if (typeof cost !== 'object' || cost === null) {
        return { accepted: false, rejectionCode: 'SPEND_COST_INVALID' };
      }
      const stock = ctx.state.resources.stock as Record<string, number>;
      for (const [res, amount] of Object.entries(cost)) {
        if (!Number.isFinite(amount) || amount < 0) {
          return { accepted: false, rejectionCode: 'SPEND_COST_INVALID' };
        }
        if ((stock[res] ?? 0) < amount) {
          return { accepted: false, rejectionCode: 'SPEND_RESOURCES_INSUFFICIENT' };
        }
      }
      for (const [res, amount] of Object.entries(cost)) {
        stock[res] = (stock[res] ?? 0) - amount;
      }
      ctx.emit({ type: 'ResourcesSpent', payload: { cost: { ...cost }, reason: payload.reason } });
      return { accepted: true };
    },
  };
}

export interface LogisticsConfig {
  /** 每 Tick 全局配送预算（资源单位总量，区域批次而非单件） */
  readonly batchPerTick: number;
  /**
   * 是否要求工地经道路连通到库房才配送（默认 true）。
   *
   * 关掉时一律视为可达 —— 这是**玩法开关**而不是实现细节：强制修路会改变
   * 游戏规则，应由设计决定何时开启，而不是被接线工作顺手带进去。
   */
  readonly requireRoads?: boolean;
}
export const DEFAULT_LOGISTICS: LogisticsConfig = { batchPerTick: 20, requireRoads: true };

/**
 * 物流系统：把 reserved 逐批搬到 delivered。
 * 只服务 planned / under_construction 且尚有未送达材料的工地；
 * 不可达工地跳过并告警（一次），恢复可达时发 DeliveryResumed。
 */
export function createLogisticsSystem(cfg: LogisticsConfig = DEFAULT_LOGISTICS): SimulationSystem {
  return {
    id: 'logistics',
    update(ctx) {
      const cons = constructionOf(ctx);
      const requireRoads = cfg.requireRoads ?? true;
      const reach = requireRoads ? reachableCellKeys(ctx, cons) : null;
      let budget = cfg.batchPerTick;
      for (const { entityId, building: b } of listBuildings(ctx.state.entities)) {
        if (b.lifecycle !== 'planned' && b.lifecycle !== 'under_construction') continue;
        const remaining = sumValues(b.reserved);
        if (remaining <= 0) continue;
        const reachable = reach === null || b.cells.some((c) => reach.has(cellKey(c)));
        if (!reachable) {
          if (b.blocked !== LOGISTICS_UNREACHABLE) {
            b.blocked = LOGISTICS_UNREACHABLE;
            ctx.emit({
              type: 'DeliveryBlocked',
              payload: { entityId, reason: LOGISTICS_UNREACHABLE },
            });
          }
          continue;
        }
        if (b.blocked === LOGISTICS_UNREACHABLE) {
          b.blocked = null;
          ctx.emit({ type: 'DeliveryResumed', payload: { entityId } });
        }
        if (budget <= 0) continue;
        // 按资源名确定序配送
        for (const res of Object.keys(b.reserved).sort()) {
          if (budget <= 0) break;
          const move = Math.min(b.reserved[res] ?? 0, budget);
          if (move <= 0) continue;
          b.reserved[res] = (b.reserved[res] ?? 0) - move;
          if (b.reserved[res] === 0) delete b.reserved[res];
          b.delivered[res] = (b.delivered[res] ?? 0) + move;
          budget -= move;
        }
        if (sumValues(b.reserved) === 0 && b.lifecycle === 'planned') {
          b.lifecycle = 'under_construction';
          ctx.emit({ type: 'ConstructionStarted', payload: { entityId } });
        }
      }
    },
  };
}

export interface ConstructionWorkConfig {
  /** 单个工地同时容纳的最大施工人数 */
  readonly maxWorkersPerSite: number;
}
export const DEFAULT_WORK: ConstructionWorkConfig = { maxWorkersPerSite: 4 };

/**
 * 施工系统：材料齐备的工地按确定序分配共享班组，累计工时；
 * 材料随进度线性消耗（delivered -> consumed，整数账）；
 * 工时达标稳定转换 operational 并发 BuildingCompleted。
 */
export function createConstructionWorkSystem(
  getDef: (defId: string) => PlanBuildingSpec | undefined,
  cfg: ConstructionWorkConfig = DEFAULT_WORK,
): SimulationSystem {
  return {
    id: 'construction-work',
    update(ctx) {
      const cons = constructionOf(ctx);
      let available = cons.workforce;
      for (const { entityId, building: b } of listBuildings(ctx.state.entities)) {
        if (b.lifecycle !== 'under_construction') continue;
        if (sumValues(b.reserved) > 0) continue;      // 材料未齐不开工
        if (available <= 0) break;
        const workers = Math.min(cfg.maxWorkersPerSite, available);
        available -= workers;
        b.progressMinutes = Math.min(
          b.workMinutes,
          b.progressMinutes + workers * MINUTES_PER_TICK,
        );
        // 材料按进度线性消耗：consumed 目标 = floor(总量 × 进度比)
        const def = getDef(b.defId);
        const ratio = b.progressMinutes / b.workMinutes;
        const total: Record<string, number> = { ...(def?.cost ?? {}) };
        for (const res of Object.keys(total).sort()) {
          const target = b.progressMinutes >= b.workMinutes
            ? total[res] ?? 0
            : Math.floor((total[res] ?? 0) * ratio);
          const cur = b.consumed[res] ?? 0;
          const move = Math.min(Math.max(0, target - cur), b.delivered[res] ?? 0);
          if (move <= 0) continue;
          b.delivered[res] = (b.delivered[res] ?? 0) - move;
          if (b.delivered[res] === 0) delete b.delivered[res];
          b.consumed[res] = cur + move;
        }
        if (b.progressMinutes >= b.workMinutes) {
          b.lifecycle = 'operational';
          b.blocked = null;
          ctx.emit({ type: 'BuildingCompleted', payload: { entityId } });
        }
      }
    },
  };
}

/** 施工缺口/预计完成（UI 投影用，纯函数不改状态）。 */
export interface ConstructionOutlook {
  readonly undelivered: Record<string, number>;
  readonly workRemaining: number;
  /** 预计还需 Tick 数（Infinity 用 -1 表达：被阻塞或无产能） */
  readonly etaTicks: number;
  readonly blocked: string | null;
}
export function constructionOutlook(
  b: BuildingComponent,
  logistics: LogisticsConfig = DEFAULT_LOGISTICS,
  work: ConstructionWorkConfig = DEFAULT_WORK,
  workforce = 8,
): ConstructionOutlook {
  const undelivered = { ...b.reserved };
  const workRemaining = Math.max(0, b.workMinutes - b.progressMinutes);
  if (b.blocked) return { undelivered, workRemaining, etaTicks: -1, blocked: b.blocked };
  const deliverTicks = Math.ceil(sumValues(undelivered) / Math.max(1, logistics.batchPerTick));
  const workersPerTick = Math.min(work.maxWorkersPerSite, Math.max(0, workforce));
  if (workRemaining > 0 && workersPerTick === 0) {
    return { undelivered, workRemaining, etaTicks: -1, blocked: b.blocked };
  }
  const workTicks = workersPerTick > 0
    ? Math.ceil(workRemaining / (workersPerTick * MINUTES_PER_TICK))
    : 0;
  return { undelivered, workRemaining, etaTicks: deliverTicks + workTicks, blocked: b.blocked };
}
